import Fuse from 'fuse.js';
import constants from '../Constants/constants';

const CACHE_KEY = 'primal_card_names';
const MAX_CACHE_SIZE = 2000;

// In-memory store for API-fetched names (populated once per session)
let liveCardNames = null;

/**
 * Fetch card names from the API endpoint. Called once per session.
 * Falls back silently — getAllCardNames() will use localStorage cache instead.
 */
export async function fetchCardNames() {
  if (liveCardNames) return;
  try {
    const res = await fetch(`${constants.DOMAIN}api/v1/guest/cardNames`, { mode: 'cors', method: 'GET' });
    const data = await res.json();
    if (Array.isArray(data.cardNames) && data.cardNames.length > 0) {
      liveCardNames = data.cardNames;
    }
  } catch {
    // Endpoint not available — localStorage cache will be used as fallback
  }
}

/**
 * Read cached card names from localStorage.
 */
export function getCachedCardNames() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Get all known card names. Priority:
 * 1. Live API names (if endpoint responded this session)
 * 2. Organically cached names from localStorage (from past search results)
 * Both sources are merged if available.
 */
function getAllCardNames() {
  const cached = getCachedCardNames();
  if (!liveCardNames) return cached;
  if (cached.length === 0) return liveCardNames;
  const merged = new Set(liveCardNames);
  for (const name of cached) {
    merged.add(name);
  }
  return Array.from(merged);
}

/**
 * Merge new card names into the localStorage cache (deduplicates, caps at MAX_CACHE_SIZE).
 */
export function cacheCardNames(cards) {
  if (!Array.isArray(cards) || cards.length === 0) return;

  const names = cards
    .map(c => c.name)
    .filter(n => typeof n === 'string' && n.trim());

  if (names.length === 0) return;

  try {
    const existing = new Set(getCachedCardNames());
    for (const name of names) {
      existing.add(name);
    }

    let arr = Array.from(existing);
    if (arr.length > MAX_CACHE_SIZE) {
      arr = arr.slice(arr.length - MAX_CACHE_SIZE);
    }

    localStorage.setItem(CACHE_KEY, JSON.stringify(arr));
  } catch {
    // localStorage quota exceeded or unavailable
  }
}

/**
 * Strip periods, hyphens, apostrophes, and other punctuation from a query.
 * "N.A.D.I.A." -> "NADIA", "Soul's Edge" -> "Souls Edge"
 */
export function normalizeQuery(query) {
  if (!query) return '';
  return query.replace(/[.\-'`,!?]/g, '').replace(/\s+/g, ' ').trim();
}

/**
 * Run Fuse.js fuzzy search against all known card names (static + cached).
 * Compares against normalized versions so "NADIA" matches "N.A.D.I.A.".
 * Returns top matches as original (un-normalized) card name strings.
 */
export function fuzzyMatchCardNames(query, limit = 5) {
  const names = getAllCardNames();
  if (names.length === 0 || !query) return [];

  const normalizedQuery = normalizeQuery(query).toLowerCase();

  const entries = names.map(name => ({
    original: name,
    normalized: normalizeQuery(name).toLowerCase(),
  }));

  // Exact normalized match (e.g. "nadia" === "nadia" from "N.A.D.I.A.")
  const exactMatches = entries.filter(e => e.normalized === normalizedQuery);
  if (exactMatches.length > 0) {
    return exactMatches.slice(0, limit).map(e => e.original);
  }

  // Fuzzy match against the normalized field
  const fuse = new Fuse(entries, {
    keys: ['normalized'],
    threshold: 0.5,
    distance: 200,
    includeScore: true,
  });

  const results = fuse.search(normalizedQuery, { limit });
  return results.map(r => r.item.original);
}
